<script lang="ts">
	import { format } from '../../../../services/i18n';
	import { getImagesByType, getImages, mapImgArray } from '../../../Lists/list-helpers';
	import { Text, Input, Loader, Container } from '@Shelly/shelly-design-system';

	import ImageList from '../../../Lists/ImageList.svelte';
	import { capFirstChar } from '../../../../helpers/utilities';
	import { devices } from 'src/stores/lists/devices';
	import type { Device } from 'src/types/Device';

	export let editDevice: Device;
	export let focusOnLoad = false;

	let uploadedImagesDB = [];
	let imagesByTypeDB = [];
	let uploadedImages = [];
	//#region Name Input functions

	const onInputName = (event: InputEvent) => {
		const target = event.target as HTMLInputElement;
		editDevice = {
			...editDevice,
			name: target.value,
		};
	};
	//#endregion

	//#region Images functions
	const onSelectImage = (e: CustomEvent) => {
		if (e.detail.icon) {
			// User selected a Font Awesome icon
			editDevice = {
				...editDevice,
				image: '',
				backgroundColor: '',
				icon: e.detail.icon,
				// Only update customIcon if this is a new custom icon
				customIcon: e.detail.isCustom ? e.detail.icon : editDevice.customIcon,
			};
		} else if (e.detail.backgroundColor) {
			// User selected a background color
			editDevice = {
				...editDevice,
				image: '',
				backgroundColor: e.detail.backgroundColor,
				icon: '',
			};
		} else {
			// User selected a regular image
			editDevice = {
				...editDevice,
				image: e.detail.image,
				backgroundColor: '',
				icon: '',
			};
		}

		// Handle shared device and local image
		if (editDevice.shared && e.detail.image) {
			editDevice.localImage = true;
		} else if (e.detail.backgroundColor || e.detail.icon) {
			delete editDevice.localImage;
		}

		// Refresh to ensure custom icon stays visible
		refreshImagesByType();
	};

	const onUploadImage = (e: CustomEvent) => {
		onSelectImage(e);
		uploadedImagesDB = [...e.detail.uploadedImagesDB];
	};

	// Handle custom icon selection from modal
	const onCustomIconSelected = (e: CustomEvent) => {
		const iconClass = e.detail.icon;

		// Update the device with the new custom icon
		editDevice = {
			...editDevice,
			image: '',
			backgroundColor: '',
			icon: iconClass,
			customIcon: iconClass, // Store as custom icon
		};

		// Refresh the images to show the custom icon
		refreshImagesByType();
	};

	// Refresh images with custom icon from device
	const refreshImagesByType = () => {
		// If device has a custom icon, ALWAYS add it to the list
		imagesByTypeDB = getImagesByType('device', editDevice.type, editDevice);
	};

	const downloadImages = async () => {
		uploadedImagesDB = await getImages('device');

		// Get images by device type and handle custom icon
		refreshImagesByType();

		uploadedImages = mapImgArray(uploadedImagesDB, 'uploaded', editDevice?.image);

		let imgDeleted = false;
		if (editDevice?.image) {
			imgDeleted = !uploadedImages.some((objImage) => objImage.image.includes(editDevice.image));

			// Check if the image is in the local images
			if (imgDeleted) {
				imgDeleted = !imagesByTypeDB.some((objImage) =>
					objImage.filepath?.includes(editDevice.image),
				);
			}
		}

		if (imgDeleted && editDevice) {
			editDevice.image = $devices[editDevice?.id]?.image;

			editDevice = {
				...editDevice,
				image: '',
			};
		}

		return uploadedImagesDB;
	};

	//#endregion Images functions
</script>

<Container type="borderSolid" backgroundColor="color-base" noScroll>
	<Text element="h2" class="mb-s text-400">
		{capFirstChar(
			$format('placeholder_name', {
				values: { type: $format('process_device') },
			}),
		)}
	</Text>
	<Input
		icon="fas fa-microchip"
		id="input-device-name"
		label={$format('label_name', { default: 'name' })}
		aria-label={$format('input_device_name', {
			default: 'Device name',
		})}
		value={editDevice.name}
		error={editDevice.name === ''
			? $format('input_error_empty_name', { default: 'Name cannot be empty' })
			: ''}
		on:input={onInputName}
	/>
</Container>

<Container type="borderSolid" backgroundColor="color-base" class="mt-m" noScroll>
	<Text element="h2" class="mb-m text-400">
		{$format('select_picture', { default: 'Select Picture' })}
	</Text>

	{#await downloadImages()}
		<div class="d-flex justify-content-center">
			<Loader />
		</div>
	{:then}
		<ImageList
			{uploadedImagesDB}
			{imagesByTypeDB}
			selectedImage={editDevice.image}
			selectedIcon={editDevice.icon}
			selectedBackgroundColor={editDevice.backgroundColor}
			requestType={'device'}
			on:select-image={onSelectImage}
			on:upload-image={onUploadImage}
			on:custom-icon-selected={onCustomIconSelected}
		/>
	{/await}
</Container>
